export type AccountHistoryType = 'history' | 'related' | 'abnormal'

export interface AccountHistorySummary {
  historicalPostCount: number
  woodpeckerRelated: number
  abnormalPostCount: number
}

export interface AccountHistoryPagination {
  page: number
  size: number
  total: number
  totalPages: number
}

export interface AccountHistoryItem {
  uniqueId: string
  postId: string
  url: string
  likeCount: number
  commentCount: number
  collectCount: number
  shareCount: number
  viewCount: number
  title: string
  avatar?: string
  postCreateTime: string
  platform: string
  postCategory: string
  content: string
  feature?: string | Record<string, unknown>
  emergencyLevel: string
  isInvalidInformation: number
  isCollect: number
  isRead: number
  isBlackPR: number
  isTransfer: number
  platformName: string
  isImport: number
  sentiment: string
  problemClass: string
  isBlock: number
  nickname: string
  sentimentAnalysis: string
  sensitivityAnalysis?: number
  opinionLevel: number
  relevant_detect?: number
  typeCollect?: string | null
  contentType?: number | string
  isMediaAllocated?: number
  marketingDetect?: string
  marketingModifyConfidence?: string
  isMarketing?: number | null
  origin?: string | Record<string, unknown> | null
}

export interface AccountHistoryResponse {
  summary: AccountHistorySummary
  pagination: AccountHistoryPagination
  items: AccountHistoryItem[]
  publisher?: AccountPublisherInfo | null
  publisherIsBlackPR?: number
}

export interface AccountPublisherInfo extends Record<string, unknown> {
  nickname?: string
  avatar?: string
  platformName?: string
  userId?: string
  userUrl?: string
}

export interface AccountPostRouteSnapshot {
  readonly fullPath: string
  readonly uniqueId: string
  readonly historyType: AccountHistoryType | null
  readonly nickname: string
  readonly parentRoute: string
}

const METRIC_HISTORY_TYPES: Record<string, AccountHistoryType> = {
  historicalPostCount: 'history',
  woodpeckerRelated: 'related',
  abnormalPostCount: 'abnormal',
}

const HISTORY_TYPE_LABELS: Record<AccountHistoryType, string> = {
  history: '历史发帖',
  related: '啄木鸟相关',
  abnormal: '异常发帖',
}

export function getAccountHistoryTypeForMetric(fieldKey: string): AccountHistoryType | null {
  return METRIC_HISTORY_TYPES[fieldKey] ?? null
}

export function getAccountHistoryTypeLabel(type: string): string | null {
  return HISTORY_TYPE_LABELS[type as AccountHistoryType] ?? null
}

export function buildAccountPostRoute(
  uniqueId: string,
  type: AccountHistoryType,
  parentRoute = `/web/public-opinion-content/${encodeURIComponent(uniqueId)}`,
  nickname = '',
) {
  const query: Record<string, string> = { parentRoute }
  const normalizedNickname = nickname.trim()
  if (normalizedNickname) query.nickname = normalizedNickname

  return {
    name: 'AccountPostList',
    params: { uniqueId, type },
    query,
  }
}

export function buildAccountPostTitle(type: string, nickname: unknown): string {
  const typeLabel = getAccountHistoryTypeLabel(type) ?? '账号帖子'
  const normalizedNickname = typeof nickname === 'string' ? nickname.trim() : ''
  return normalizedNickname ? `${typeLabel}-${normalizedNickname}` : typeLabel
}

function normalizeRouteString(value: unknown): string {
  if (typeof value === 'string') return value.trim()
  if (Array.isArray(value) && typeof value[0] === 'string') return value[0].trim()
  return ''
}

export function createAccountPostRouteSnapshot(route: {
  fullPath: string
  uniqueId: unknown
  type: unknown
  nickname?: unknown
  parentRoute?: unknown
}): AccountPostRouteSnapshot {
  const uniqueId = normalizeRouteString(route.uniqueId)
  const type = normalizeRouteString(route.type)
  const historyType = getAccountHistoryTypeLabel(type) ? type as AccountHistoryType : null
  const parentRoute = resolveAccountPostParentRoute(
    route.fullPath,
    normalizeRouteString(route.parentRoute) || undefined,
    uniqueId,
  )

  return Object.freeze({
    fullPath: route.fullPath,
    uniqueId,
    historyType,
    nickname: normalizeRouteString(route.nickname),
    parentRoute,
  })
}

export function resolveAccountPostParentRoute(
  currentFullPath: string,
  sourceParentRoute: string | undefined,
  fallbackUniqueId?: string,
): string {
  const normalizedSourceRoute = sourceParentRoute?.trim()
  if (normalizedSourceRoute) return normalizedSourceRoute
  if (fallbackUniqueId) {
    return `/web/public-opinion-content/${encodeURIComponent(fallbackUniqueId)}`
  }
  return currentFullPath
}

export function resolveAccountPublisherInfo(
  userInfo: AccountPublisherInfo | null | undefined,
  firstItem: AccountHistoryItem | undefined,
): AccountPublisherInfo | null {
  if (userInfo) return userInfo
  if (!firstItem) return null

  return {
    nickname: firstItem.nickname,
    avatar: firstItem.avatar,
    platformName: firstItem.platformName,
  }
}

export function getAccountHistoryErrorMessage(error: unknown): string {
  const businessMessage = error instanceof Error ? error.message : ''
  const responseMessage = typeof error === 'object' && error !== null
    ? (error as { response?: { data?: { msg?: string } } }).response?.data?.msg
    : undefined
  const code = businessMessage || responseMessage

  if (code === 'OPINION_NOT_FOUND') return '未找到对应的舆情内容。'
  if (code === 'ACCOUNT_IDENTITY_MISSING') return '当前帖子缺少可用于匹配账号的信息。'
  return '账号帖子加载失败，请稍后重试。'
}

const DATABASE_SUMMARY_FIELDS = new Set<keyof AccountHistorySummary>([
  'historicalPostCount',
  'woodpeckerRelated',
  'abnormalPostCount',
])

export function resolvePublisherMetricValue(
  fieldKey: string,
  userInfo: Record<string, unknown> | null,
  summary: AccountHistorySummary | null,
) {
  if (summary && DATABASE_SUMMARY_FIELDS.has(fieldKey as keyof AccountHistorySummary)) {
    return summary[fieldKey as keyof AccountHistorySummary]
  }
  return userInfo?.[fieldKey]
}
